import { Platform } from 'react-native'
import {
  pick,
  types,
  isErrorWithCode,
  errorCodes,
  keepLocalCopy,
} from '@react-native-documents/picker'
import { Bugsnag } from '@src/config'
import { ReactNativeFile } from './image'

/**
 * Fully percent-decodes the file:// URI returned by keepLocalCopy so it can be used as
 * a filesystem path (react-native-blob-util's fs.stat/hash take a path, not a URI).
 *
 * `decodeURI` is not enough: by spec it preserves the escapes of reserved characters
 * (`, # & + = $ : ; ? @`), so "Results May 20, 2026.pdf" survives as
 * "Results May 20%2C 2026.pdf" and every later fs call fails with "failed to stat path".
 * Spaces decode either way, which is why the space case has always worked.
 *
 * A filename can legitimately contain a stray `%`, which makes the URI malformed and
 * decodeURIComponent throw — fall back to the raw URI rather than losing the pick.
 */
export const decodeLocalFileUri = (uri: string) => {
  try {
    return decodeURIComponent(uri)
  } catch {
    return uri
  }
}

export const pickDocument = async (onSelect: (file: ReactNativeFile) => void) => {
  if (Platform.OS === 'web') {
    console.warn('Document picking is not supported on web.')
    return
  }

  try {
    const res = await pick({
      type: [types.pdf, types.doc, types.docx],
      allowMultiSelection: false,
    })
    const file = res[0]
    const fileName = file.name || 'Untitled'

    const [copyResult] = await keepLocalCopy({
      files: [
        {
          uri: file.uri,
          fileName,
        },
      ],
      destination: 'documentDirectory',
    })
    if (copyResult.status === 'success') {
      const decodedLocalUri = decodeLocalFileUri(copyResult.localUri)
      onSelect(
        new ReactNativeFile({
          uri: decodedLocalUri,
          name: fileName,
          type: file.type || 'application/pdf',
          sizeInBytes: file.size ?? undefined,
        }),
      )
    } else {
      throw new Error(`Failed to keep a local copy of the document: ${copyResult.copyError}`)
    }
  } catch (e) {
    handleError(e)
  }
}

const handleError = (err: unknown) => {
  if (isErrorWithCode(err)) {
    if (err.code === errorCodes.OPERATION_CANCELED) {
      // ignore
      return
    }
  }
  Bugsnag.notify(String(err))
}
